import React, { useEffect, useState } from "react";
import { Filter } from "react-feather";
import CommonModal from "../../../../CommonElements/Functions/CommonModels";
import TreeView from "react-accessible-treeview";
import { Card, CardBody, Col } from "reactstrap";
import CommonCardHeader from "../../../../Utils/CommonComponents/CommonCardHeader";
import { basicTreeSubTitle } from "../../../../Data/BonusUiData/TreeViewData";
import { BasicTreeTitle } from "../../../../Utils/Constants";
import {
  ArrowIcon,
  CheckBoxIcon,
} from "../../../BonusUi/TreeView/Common/ArrowIcon";
import {
  ensureDexieReady,
  getDataFromDexie,
  getDataFromDexieArray,
} from "../../../../db/DexieServises";
import { INode } from "react-accessible-treeview";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../../../redux-toolkit/store";
import { setPositions } from "../../../../redux-toolkit/positionSlice";

interface TreeModelProps {
  onSelectionChange?: (ids: (string | number)[]) => void;
}

export default function TreeModelReport({ onSelectionChange }: TreeModelProps) {
  const dispatch = useDispatch();
  const [modal, setModal] = useState(false);
  const toggle = () => setModal(!modal);
  const [treeData, setTreeData] = useState<INode<any>[]>([]);
  const [selectedIds, setSelectedIds] = useState<(string | number)[]>([]);
  const [rawData, setRawData] = useState<any[]>([]);
  const unitName = useSelector((state: RootState) => state.orgUnit.name);
  const unitId = useSelector((state: RootState) => state.orgUnit.id);
  useEffect(() => {
    const loadPositionData = async () => {
      const tableName = "Position";

      try {
        await ensureDexieReady();

        var rawData = await getDataFromDexieArray("Position", "PositionId");
        const filtered = Object.values(rawData).filter(
          (item: any) => item.OrgUnitId === unitId
        );
        setRawData(filtered);

        const result: Record<number, any> = {};
        filtered.forEach((row) => {
          result[row["PositionId"]] = {
            id: row["PositionId"],
            name: row["PositionName"],
            parent: row["ReportingTo"],
            children: [],
          };
        });

        // Ensure placeholder root for broken links: prefer id 0, fallback to -1
        const PLACEHOLDER_ID: number = result[0] ? -1 : 0;
        if (!result[PLACEHOLDER_ID]) {
          result[PLACEHOLDER_ID] = {
            id: PLACEHOLDER_ID,
            name: "Unassigned",
            parent: null,
            children: [],
          };
        } else {
          result[PLACEHOLDER_ID].parent = null;
          result[PLACEHOLDER_ID].name =
            result[PLACEHOLDER_ID].name || "Unassigned";
          result[PLACEHOLDER_ID].children =
            result[PLACEHOLDER_ID].children || [];
        }

        // Helper to detect cycles
        const createsCycle = (
          currentId: number,
          parentId: number | null
        ): boolean => {
          if (parentId == null) return false;
          if (parentId === currentId) return true;
          const seen = new Set<number>([currentId]);
          let cur: number | null | undefined = parentId;
          while (cur != null) {
            if (seen.has(cur)) return true;
            seen.add(cur);
            const next: number | null | undefined = result[cur]?.parent as
              | number
              | null
              | undefined;
            if (next != null && result[next] === undefined) return false; // broken chain ends
            cur = next ?? null;
          }
          return false;
        };

        // Sanitize parents: missing/self-parent/cycles -> attach to placeholder
        Object.values(result).forEach((node) => {
          if (!node) return;
          if (node.id === PLACEHOLDER_ID) return;
          let p: number | null = node.parent as number | null;
          const missing = p != null && result[p] === undefined;
          const selfParent = p === node.id;
          const hasCycle = createsCycle(node.id, p);
          if (missing || selfParent || hasCycle || p == null) {
            p = PLACEHOLDER_ID;
          }
          node.parent = p;
        });

        // Rebuild children arrays
        Object.values(result).forEach((n) => {
          if (n) n.children = [];
        });
        Object.values(result).forEach((node) => {
          if (!node) return;
          const p: number | null = node.parent as number | null;
          if (p != null && result[p]) {
            result[p].children.push(node.id);
          }
        });

        const unlinkedPositions = filtered.filter(
          (row) =>
            !result[row.PositionId].parent &&
            result[row.PositionId].children.length === 0
        );
        // console.log('❌ Unlinked Positions (No Parent, No Children):', unlinkedPositions);

        // Filter out unlinked positions without children from the tree data
        const treeData: INode<any>[] = Object.values(result).filter((node) => {
          const isUnlinked = node.parent === null && node.children.length === 0;
          if (isUnlinked) {
            console.log("❌ Excluding Unlinked Position:", node);
          }
          return !isUnlinked;
        });
        console.log("✅ Filtered Data: treeData", treeData);
        setTreeData(treeData);
      } catch (error) {
        console.error(
          `❌ Failed to load or parse schema/data for ${tableName}`,
          error
        );
      }
    };

    loadPositionData();
  }, [unitId]);

  const getAllParentIds = (data: any[]) => {
    return data
      .filter((node) => node.children && node.children.length > 0)
      .map((node) => node.id);
  };
  const parentIds = getAllParentIds(treeData);

  const uniqueNames = Array.from(
    new Set([
      ...selectedIds
        .map((id) => rawData.find((row) => row.PositionId === id))
        .filter(
          (row): row is { PositionName: string; ReportingTo?: number } =>
            row !== undefined
        )
        .sort((a, b) => {
          if (a.ReportingTo === null && b.ReportingTo !== null) return -1; // Parent nodes first
          if (a.ReportingTo !== null && b.ReportingTo === null) return 1;
          return (a.ReportingTo || 0) - (b.ReportingTo || 0); // Sort by hierarchy
        })
        .map((row) => row.PositionName),
      ...useSelector((state: RootState) => state.position.positions)
        .filter(
          (pos): pos is { id: number; name: string; ReportingTo?: number } =>
            true
        )
        .sort((a, b) => {
          if (a.ReportingTo === null && b.ReportingTo !== null) return -1; // Parent nodes first
          if (a.ReportingTo !== null && b.ReportingTo === null) return 1;
          return (a.ReportingTo || 0) - (b.ReportingTo || 0); // Sort by hierarchy
        })
        .map((pos) => pos.name),
    ])
  ).sort((a, b) => {
    const parentA = rawData.find((row) => row.PositionName === a)?.ReportingTo;
    const parentB = rawData.find((row) => row.PositionName === b)?.ReportingTo;
    if (parentA === null && parentB !== null) return -1; // Parent nodes first
    if (parentA !== null && parentB === null) return 1;
    return (parentA || 0) - (parentB || 0); // Sort by hierarchy
  });

  const [displayedSelectedId, setDisplayedSelectedId] = useState<
    string | number | null
  >(null); // Track the displayed selected node

  const handleNodeSelect = (selectedNodeId: string | number) => {
    const selectedNode = treeData.find((node) => node.id === selectedNodeId);

    if (!selectedNode) return;

    // Select all child nodes if a parent node is selected
    const selectAllChildren = (node: INode<any>): (string | number)[] => {
      const children = node.children || [];
      return [
        node.id,
        ...children.flatMap((childId) => {
          const childNode = treeData.find((n) => n.id === childId);
          return childNode ? selectAllChildren(childNode) : [];
        }),
      ];
    };

    const newSelectedIds = selectAllChildren(selectedNode);
    setSelectedIds(newSelectedIds);
    setDisplayedSelectedId(selectedNodeId); // Highlight the parent node in TreeView
  };

  useEffect(() => {
    if (modal) {
      setSelectedIds([...selectedIds]); // Trigger re-render of TreeView with updated selectedIds
    }
  }, [modal]);

  const getAllExpandedIds = (data: INode<any>[]): (string | number)[] => {
    const expandedIds: (string | number)[] = [];
    const traverse = (node: INode<any>) => {
      if (node.children && node.children.length > 0) {
        expandedIds.push(node.id);
        node.children.forEach((childId) => {
          const childNode = data.find((n) => n.id === childId);
          if (childNode) traverse(childNode);
        });
      }
    };
    data.forEach((node) => traverse(node));
    return expandedIds;
  };

  const defaultExpandedIds = getAllExpandedIds(treeData);

  // Compose selection label up to a character limit
  const formatSelectionLabel = (names: string[], maxChars = 20) => {
    const full = names.join(", ");
    if (full.length <= maxChars) return full || "";
    return (
      full
        .slice(0, maxChars)
        .replace(/[\s,]+$/, "")
        .trimEnd() + "..."
    );
  };

  return (
    <>
      <div
        className="light-box bg-primary"
        style={{
          cursor: "pointer",
          borderRadius: "5px",
          padding: "7px",
          paddingRight: "10px",
          paddingLeft: "10px",
        }}
        onClick={toggle}
        title={uniqueNames.join(", ")}
      >
        <Filter size={18} />
        <span style={{ marginLeft: "10px", color: "white" }}>
          {formatSelectionLabel(uniqueNames, 20)}
        </span>
      </div>

      <CommonModal
        isOpen={modal}
        toggle={toggle}
        saveText="Apply"
        title="Position Tree"
        showFooter={true}
      >
        <div className="d-flex justify-content-end align-items-center">
          <button
            className="btn btn-secondary"
            style={{ marginLeft: "10px" }}
            onClick={() => {
              setSelectedIds([]); // Clear all selected filters
              // setModal(!modal);
              if (onSelectionChange) onSelectionChange(selectedIds);
            }}
          >
            Clear
          </button>
          <button
            className="btn btn-primary"
            style={{ marginLeft: "10px" }}
            onClick={() => {
              const selectedData = rawData.filter((row) =>
                selectedIds.includes(row.PositionId)
              );
              // Dispatch selected positions to Redux
              dispatch(
                setPositions(
                  selectedData.map((row) => ({
                    id: row.PositionId,
                    name: row.PositionName,
                  }))
                )
              );
              setModal(!modal);
              if (onSelectionChange) onSelectionChange(selectedIds);
            }}
          >
            Apply
          </button>
        </div>
        <Col sm={12}>
          <CardBody>
            {treeData.length > 0 ? (
              <TreeView
                data={treeData}
                aria-label="Checkbox tree"
                multiSelect={true} // Enable multi-select for checkbox behavior
                propagateSelect={true}
                propagateSelectUpwards={true}
                togglableSelect={true}
                selectedIds={selectedIds} // Highlight all selected nodes
                defaultExpandedIds={defaultExpandedIds} // Expand all nodes by default
                onSelect={({ treeState }) => {
                  const selectedNodeIds = Array.from(treeState.selectedIds); // Get all selected nodes
                  setSelectedIds(selectedNodeIds);
                }}
                nodeRenderer={(nodeProps) => {
                  const {
                    element,
                    isBranch,
                    isExpanded,
                    isSelected,
                    getNodeProps,
                    level,
                    handleExpand,
                  } = nodeProps;

                  return (
                    <div
                      {...getNodeProps({ onClick: handleExpand })}
                      style={{ marginLeft: 40 * (level - 1) }}
                    >
                      {isBranch && <ArrowIcon isOpen={isExpanded} />}
                      <input
                        type="checkbox"
                        name="treeNode"
                        checked={isSelected}
                        onChange={() => {
                          const newSelectedIds = isSelected
                            ? selectedIds.filter((id) => id !== element.id)
                            : [...selectedIds, element.id];
                          setSelectedIds(newSelectedIds);
                        }}
                      />
                      <span className="name ms-2">
                        {element?.name || "Unnamed"}
                      </span>
                    </div>
                  );
                }}
              />
            ) : (
              <div>Loading tree...</div>
            )}
          </CardBody>
        </Col>
        <div className="d-flex justify-content-end align-items-center">
          <button
            className="btn btn-secondary"
            style={{ marginLeft: "10px" }}
            onClick={() => {
              setSelectedIds([]); // Clear all selected filters
            }}
          >
            Clear
          </button>
          <button
            className="btn btn-primary"
            style={{ marginLeft: "10px" }}
            onClick={() => {
              const selectedData = rawData.filter((row) =>
                selectedIds.includes(row.PositionId)
              );
              // Dispatch selected positions to Redux
              dispatch(
                setPositions(
                  selectedData.map((row) => ({
                    id: row.PositionId,
                    name: row.PositionName,
                  }))
                )
              );
              setModal(!modal);
              if (onSelectionChange) onSelectionChange(selectedIds);
            }}
          >
            Apply
          </button>
        </div>
      </CommonModal>
    </>
  );
}
